import {css, StyleSheet} from 'aphrodite';
import React, {CSSProperties, useLayoutEffect, useRef} from 'react';

import {AlignmentBoxAndWhiskers} from 'search/blast/AlignmentBoxAndWhiskers';
import {DragSelected} from 'search/blast/BLASTAlignmentSelectHelper';
import {getAlignmentScoreColor, getTotalComponentSize, divScrollTo} from 'search/blast/BLASTAlignmentUtil';
import {AlignmentConstants} from 'search/blast/constants';
import GraphAlignmentRuler from 'search/blast/GraphAlignmentRuler';
import GraphDragZoomScreen from 'search/blast/GraphDragZoomScreen';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_SEMIDARK} from 'ui-library/Colors';

export interface GraphAlignmentProps {
  queryLength: number;
  hitStart: number; // 1-based index
  hitEnd: number; // 1-based index
  zoomLevel: number;
  alignmentScore: number;
  scroll: number;
  rowHeight: number; // in pixels
  rowWidth: number; // in pixels
  isCompact: boolean;
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void;
  dragSelected?: DragSelected;
  isQuery?: boolean;
  enableTooltip?: boolean;
  showRuler?: boolean;
}

/**
 * This component is a wrapper for AlignmentBoxAndWhiskers and GraphAlignmentRuler.
 * It is responsible for calculating the pixel length based on the input parameters.
 */
export default function GraphAlignment(props: GraphAlignmentProps): JSX.Element {
  const color = props.isQuery ? GRAY_SEMIDARK : getAlignmentScoreColor(props.alignmentScore);

  const scale = getTotalComponentSize(props.zoomLevel, props.rowWidth, props.queryLength) / props.queryLength;
  const boxStart = props.hitStart <= 1 ? 0 : props.hitStart * scale;
  const boxEnd = props.hitEnd * scale;
  const totalLength = props.queryLength * scale;
  const enableTooltip = props.enableTooltip ?? true;

  const outer: CSSProperties = {
    width: `${props.rowWidth}px`,
  };

  const inner: CSSProperties = {
    display: 'flex',
    width: `${totalLength}px`,
    height: `${props.rowHeight}px`,
    flexDirection: 'column',
    justifyContent: 'center',
    gap: props.isCompact ? AlignmentConstants.columnGap.compact : AlignmentConstants.columnGap.normal,
  };

  const divRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    divScrollTo(divRef, props.scroll);
  });

  return (
    <div
      {...getTestProps({component: 'GraphAlignment', element: 'root'})}
      className={css(
        props.showRuler && props.isCompact && styles.compactRowWithRuler,
        props.showRuler && !props.isCompact && styles.rowWithRuler,
        styles.behindChildren,
        styles.noScrollbars
      )}
      style={outer}
      ref={divRef}
      onMouseDown={props.handleDragStart}
    >
      <div style={inner}>
        {props.showRuler && (
          <GraphAlignmentRuler scale={scale} sequenceLength={props.queryLength} isCompact={props.isCompact} />
        )}
        <AlignmentBoxAndWhiskers
          boxStart={boxStart}
          boxEnd={boxEnd}
          totalLength={totalLength}
          color={color}
          alignmentScore={props.alignmentScore}
          enableTooltip={enableTooltip}
        />
        {props.dragSelected && (
          <GraphDragZoomScreen
            dragSelected={props.dragSelected}
            scale={scale}
            totalLength={totalLength}
            isQuery={props.isQuery}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rowWithRuler: {
    height: AlignmentConstants.columnHeight.normal,
  },
  compactRowWithRuler: {
    height: AlignmentConstants.columnHeight.compact,
  },
  behindChildren: {
    position: 'relative',
    zIndex: 0,
  },
  noScrollbars: {
    overflow: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
});
